import React, { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  InputAdornment,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { isAxiosError } from 'axios';

import { DriverStatus, type AccountSetupLink, type CreateDriverRequest } from '@kangaroo/shared';
import { StatusBadge, SectionCard, colors } from '@kangaroo/ui';

import {
  allDriversKey,
  createDriver,
  fetchAllDrivers,
} from '../../features/drivers';
import { currentTeamKey, fetchCurrentTeam } from '../../features/teams';
import { canManageDrivers } from '../../lib/roles';
import { viewerIsTeamWorkspaceOwner } from '../../lib/teamWorkspace';
import { useAuthStore } from '../../store/authStore';
import { AddDriverDialog } from './AddDriverDialog';
import { TableRowsSkeleton } from '../../components/loading/PageSkeletons';

const AVATAR_ACCENTS = ['#F97316', '#EF4444', '#F59E0B', '#22C55E', '#0EA5E9', '#A855F7', '#EC4899', '#8B5CF6'];

const panelSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2.5,
};

function driverBadgeStatus(status: DriverStatus): 'active' | 'injured' | 'inactive' {
  if (status === DriverStatus.INJURED) return 'injured';
  if (status === DriverStatus.ACTIVE) return 'active';
  return 'inactive';
}

function driverBadgeLabel(status: DriverStatus): string | undefined {
  if (status === DriverStatus.GRADUATED) return 'Graduated';
  return undefined;
}

function formatDriverNumber(driverNumber: string | null) {
  if (!driverNumber) return '—';
  return driverNumber.startsWith('#') ? driverNumber : `#${driverNumber}`;
}

function getApiErrorMessage(error: unknown) {
  if (isAxiosError(error)) {
    return (error.response?.data as { message?: string } | undefined)?.message ?? error.message;
  }
  return error instanceof Error ? error.message : 'Request failed';
}

export function DriversPage({
  embedded = false,
  onSelectDriver,
  onAddDriver,
}: {
  embedded?: boolean;
  onSelectDriver?: (driverId: string) => void;
  onAddDriver?: () => void;
} = {}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { data: team } = useSWR(user?.teamId ? currentTeamKey : null, fetchCurrentTeam);
  const isWorkspaceOwner = viewerIsTeamWorkspaceOwner(user, team);
  const canAddDrivers = user ? canManageDrivers(user.role, { isTeamWorkspaceOwner: isWorkspaceOwner }) : false;

  const [search, setSearch] = useState('');
  const [addDriverOpen, setAddDriverOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [setupLinks, setSetupLinks] = useState<AccountSetupLink[]>([]);

  const listQuery = useMemo(() => ({ status: 'ALL' as const }), []);

  const {
    data: driverDirectory,
    error: driversError,
    isLoading: driversLoading,
    mutate: mutateDrivers,
  } = useSWR(allDriversKey(listQuery), () => fetchAllDrivers(listQuery));

  const drivers = useMemo(() => {
    const directory = driverDirectory?.drivers ?? [];
    const query = search.trim().toLowerCase();
    if (!query) return directory;

    return directory.filter((driver) => (
      driver.name.toLowerCase().includes(query)
      || driver.email.toLowerCase().includes(query)
      || (driver.driverNumber?.toLowerCase().includes(query) ?? false)
    ));
  }, [driverDirectory?.drivers, search]);
  const totalDrivers = drivers.length;

  async function handleCreateDriver(payload: CreateDriverRequest) {
    try {
      const created = await createDriver(payload);
      await mutateDrivers();
      setSetupLinks(created.accountSetupLinks);
      setSuccessMessage(
        created.accountSetupLinks.length > 0
          ? `${created.driver.name} was added. Send the setup link before it expires.`
          : `${created.driver.name} was added. They can sign in on mobile with ${created.driver.email}.`,
      );
    } catch (error) {
      throw new Error(getApiErrorMessage(error));
    }
  }

  async function copySetupLink(link: AccountSetupLink) {
    await navigator.clipboard.writeText(link.registrationLink);
    setSuccessMessage(`Setup link copied for ${link.email}.`);
  }

  function handleDriverClick(driverId: string) {
    if (onSelectDriver) {
      onSelectDriver(driverId);
      return;
    }
    navigate(`/drivers/${driverId}`);
  }

  function handleAddDriverClick() {
    if (onAddDriver) {
      onAddDriver();
      return;
    }
    setAddDriverOpen(true);
  }

  function renderDriverToolbar(compact = false) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <TextField
          placeholder={compact ? 'Search drivers…' : 'Search by name or email…'}
          size="small"
          value={search}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: compact ? 200 : 220,
            '& .MuiOutlinedInput-root': {
              bgcolor: isDark ? undefined : '#FFFFFF',
              '& fieldset': { borderColor: isDark ? undefined : colors.grey[300] },
              '&:hover fieldset': { borderColor: isDark ? undefined : colors.grey[400] },
            },
          }}
        />
        {canAddDrivers && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{ px: 1.75, fontWeight: 700 }}
            onClick={handleAddDriverClick}
          >
            Add Driver
          </Button>
        )}
      </Stack>
    );
  }

  function renderDriverTable(showFooter = false) {
    return (
      <>
        {driversError && (
          <Alert severity="error" sx={{ m: 2 }}>
            {getApiErrorMessage(driversError)}
          </Alert>
        )}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Driver', '#', 'Age', 'Category', 'Email', 'Parent / Guardian', 'Status'].map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      py: 1.4,
                      color: isDark ? 'text.secondary' : colors.grey[600],
                      fontSize: '0.67rem',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      bgcolor: isDark ? undefined : colors.grey[50],
                      borderBottomColor: 'divider',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {driversLoading ? (
                <TableRowsSkeleton rows={6} columns={7} avatarColumn />
              ) : drivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 5 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>
                        No drivers yet
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 2 }}>
                        Add a driver profile to create their account. Minor drivers can be parent-linked or team-managed.
                      </Typography>
                      {canAddDrivers && (
                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddDriverClick}>
                          Add Driver
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : drivers.map((driver, index) => {
                const avatarAccent = AVATAR_ACCENTS[index % AVATAR_ACCENTS.length]!;
                const parent = driver.parents[0];

                return (
                  <TableRow
                    key={driver.id}
                    hover
                    onClick={() => handleDriverClick(driver.id)}
                    sx={{
                      cursor: 'pointer',
                      '& td': { borderBottomColor: 'divider', py: 1.25 },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: isDark ? '#F8FAFC' : avatarAccent,
                            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : `${avatarAccent}18`,
                            border: `1px solid ${avatarAccent}${isDark ? '55' : '44'}`,
                          }}
                        >
                          {driver.initials}
                        </Avatar>
                        <Typography variant="body2" fontSize="0.78rem" fontWeight={600} noWrap>
                          {driver.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                      {formatDriverNumber(driver.driverNumber)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: 'text.primary' }}>{driver.age ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: 'text.primary' }}>{driver.category}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{driver.email}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: 'text.primary' }}>
                      {parent ? (
                        <Box>
                          <Typography fontSize="0.78rem" color="text.primary">{parent.name}</Typography>
                          <Typography fontSize="0.65rem" color="text.secondary">{parent.email}</Typography>
                        </Box>
                      ) : driver.teamManaged ? (
                        <Typography color="text.secondary">Team managed</Typography>
                      ) : (
                        <Typography color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={driverBadgeStatus(driver.status)}
                        customLabel={driverBadgeLabel(driver.status)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {showFooter ? (
          <Box sx={{ px: 3, py: 1.75, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {driversLoading ? 'Loading drivers…' : `${totalDrivers} driver${totalDrivers === 1 ? '' : 's'} registered`}
            </Typography>
          </Box>
        ) : totalDrivers > 0 ? (
          <Box sx={{ px: 2.25, py: 1.2 }}>
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              {totalDrivers} driver{totalDrivers === 1 ? '' : 's'} registered
            </Typography>
          </Box>
        ) : null}
      </>
    );
  }

  return (
    <Box>
      {!embedded && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
          <Box sx={{ maxWidth: 560 }}>
            <Typography variant="h5" fontWeight={700}>Drivers</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Register drivers, parent-linked minors, and team-managed driver accounts. Each driver gets a login used on mobile
              to scan and sync their MyChron data.
            </Typography>
          </Box>
          {renderDriverToolbar()}
        </Box>
      )}

      {!embedded && !canAddDrivers && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only team owners and coaches can register new drivers.
        </Alert>
      )}

      {setupLinks.length > 0 && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => setSetupLinks([])}>Dismiss</Button>}
        >
          <Stack spacing={1}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>Registration setup links</Typography>
            {setupLinks.map((link) => (
              <Box key={`${link.userId}-${link.email}`} sx={{ display: 'flex', gap: 1.25, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{link.email}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Expires {new Date(link.expiresAt).toLocaleDateString()}</Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={() => copySetupLink(link)}>Copy link</Button>
              </Box>
            ))}
          </Stack>
        </Alert>
      )}

      {embedded ? (
        <SectionCard
          title="Drivers"
          subtitle="Driver roster, parent links, and session access."
          action={renderDriverToolbar(true)}
          contentSx={{ p: 0, '&:last-child': { pb: 0 } }}
        >
          {renderDriverTable(true)}
        </SectionCard>
      ) : (
        <Box sx={[panelSx, { minWidth: 0 }]}>
          {renderDriverTable(false)}
        </Box>
      )}

      {canAddDrivers && !onAddDriver && (
        <AddDriverDialog
          open={addDriverOpen}
          onClose={() => setAddDriverOpen(false)}
          onSubmit={handleCreateDriver}
        />
      )}

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
